/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import Styles from '../../css/LoanInformation.module.css';
import EnvelopeWithBox from '../EnvelopeWithBox';
import { useLoanInformationContext } from '../../contexts/LoanInformationContext';
import { NumericFormat } from 'react-number-format';

const LoanBalances = () => {
  const { servicingData, lnptData, lastYearLnptPayData, lnptPayData, handleLnptPayChange } =
    useLoanInformationContext();

  return (
    <EnvelopeWithBox blockName='Loan Balances Information'>
      <div className={Styles.loanInfoTable}>
        <div className={Styles.leftColumn}>
          <table className={Styles.loanInfoTableSection}>
            <tbody>
              <tr>
                <th>Unpaid Principal: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptData?.upb ? lnptData?.upb : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>YTD MIP: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptData?.miPaidYTD ? lnptData?.miPaidYTD : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Interest Paid Year-To-Date:</th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptData?.interestPaidYTD ? lnptData?.interestPaidYTD : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Current Escrow Balance:</th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptData?.escrowRecdBal ? lnptData?.escrowRecdBal : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Escrow Balance 2: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptData?.escrow2Bal ? lnptData?.escrow2Bal : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Escrow Balance 3: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptData?.escrow3Bal ? lnptData?.escrow3Bal : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Regular Suspense: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptData?.suspenseBal ? lnptData?.suspenseBal : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Other Suspense: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptData?.otherSuspenseBal ? lnptData?.otherSuspenseBal : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Subsidy Suspense:</th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptData?.subSuspenseBal ? lnptData?.subSuspenseBal : 0}
                    thousandSeparator={true}
                    prefix={'$'}
                    decimalScale={2}
                    fixedDecimalScale={true}
                  />
                </td>
              </tr>
              <tr>
                <th>Loss Drafts:</th>
                <td>
                  <NumericFormat
                    name='lossDraftBalance'
                    displayType='text'
                    value={lnptData?.lossDraftBalance ? lnptData?.lossDraftBalance : 0}
                    placeholder='Loss Drafts Amount'
                    thousandSeparator={true}
                    prefix={'$'}
                    decimalScale={2}
                    fixedDecimalScale={true}
                  />
                </td>
              </tr>
              <tr>
                <th>Unpaid Late Charges: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptData?.lcAcrdBal ? lnptData?.lcAcrdBal : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
            </tbody>
            <tr>
              <td colSpan={2} style={{ height: '20px' }}></td>
            </tr>
          </table>
        </div>
        <div className={Styles.rightColumn}>
          <table className={Styles.loanInfoTableSection}>
            <tbody>
              <tr>
                <th>Assessments: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptData?.assessmentBal ? lnptData?.assessmentBal : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Corporate Advances: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptData?.corpAdvBal ? lnptData?.corpAdvBal : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Deferred Interest: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptData?.deferInterestBal ? lnptData?.deferInterestBal : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Deferred Principal:</th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptData?.deferPrincipalBal ? lnptData?.deferPrincipalBal : 0}
                    placeholder='Deferred Principal'
                    thousandSeparator={true}
                    prefix={'$'}
                    decimalScale={2}
                    fixedDecimalScale={true}
                  />
                </td>
              </tr>
              <tr>
                <th>Deferred Payment:</th>
                <td>
                  <NumericFormat
                    name='deferPaymentBal'
                    value={lnptPayData?.deferPaymentBal ?? ''}
                    thousandSeparator
                    prefix='$'
                    decimalScale={2}
                    fixedDecimalScale
                    disabled={lnptPayData?.active ? false : true}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const num = parseFloat(raw.replace(/[^0-9.-]/g, ''));
                      handleLnptPayChange('edit', 'deferPaymentBal', Number.isNaN(num) ? null : num);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Sales Transfer Balance:</th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={servicingData?.saleTransferBalance ? servicingData?.saleTransferBalance : 0}
                    placeholder='Sales Transfer Balance'
                    thousandSeparator={true}
                    prefix={'$'}
                    decimalScale={2}
                    fixedDecimalScale={true}
                  />
                </td>
              </tr>
              <tr>
                <th>Repair Balance: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptData?.repairBalance ? lnptData?.repairBalance : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Last Year Interest: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={(lastYearLnptPayData?.interestPaidYTD ?? 0) + (lastYearLnptPayData?.interestRecd ?? 0)}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Last Year Prepaid Interest: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={(lastYearLnptPayData?.prepaidIntBal ?? 0) + (lastYearLnptPayData?.prepaidIntMonth ?? 0)}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Last Year MIP Paid: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={(lastYearLnptPayData?.miPaidYTD ?? 0) + (lastYearLnptPayData?.MIPPaidMon ?? 0)}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Interest Rate: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={lnptPayData?.intRate ? lnptPayData?.intRate : 0}
                    decimalScale={4}
                    fixedDecimalScale
                    suffix='%'
                    thousandSeparator
                  />
                </td>
              </tr>
            </tbody>
            <tr>
              <td colSpan={2} style={{ height: '20px' }}></td>
            </tr>
          </table>
        </div>
      </div>
    </EnvelopeWithBox>
  );
};

export default LoanBalances;
